import { useState, useMemo, useEffect } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle, Info, Lock } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { RiskMatrixTable } from "@/components/risk/RiskMatrixTable";
import { RiskMatrixCarousel } from "@/components/risk/RiskMatrixCarousel";
import { CyFunLevelBadge } from "@/components/risk/CyFunLevelBadge";
import { recalculateAnalysisMetrics } from "@/utils/risk-calculator";
import { formatScore } from "@/lib/utils";
import { compareSectorAnalysis } from "@/utils/risk-diff";
import { useGetDefaultMatrix } from "@/hooks/api/query/useGetDefaultMatrix";
import { processMatrixData } from "@/utils/risk-analysis-transform";
import { useSectorMatrixLocalState } from "@/hooks/risk-wizard/useSectorMatrixLocalState";
import { RiskMatrixSkeleton } from "@/components/risk/RiskMatrixSkeleton";
import type { SectorAnalysis, OrganizationSize } from "@/types/risk-analysis";

type SectorMatrixStepProps = {
  sectorId: string;
  sectorCode: string;
  sectorName: string;
  sessionId: string;
  organizationSize: OrganizationSize | null;
  serverAnalysis: SectorAnalysis | null;
  isSectorCompleted: boolean;
  isSectorDefault: boolean;
  onAnalysisChange: (analysis: SectorAnalysis | null) => void;
  onMatrixStateChange: (state: {
    isLocked: boolean;
    isDefault: boolean;
    hasChanges: boolean;
  }) => void;
  onBaseAnalysisChange: (analysis: SectorAnalysis | null) => void;
  unlockRequested: boolean;
  onUnlockRequestHandled: () => void;
  resetAnalysis: SectorAnalysis | null;
  onResetHandled: () => void;
};

export function SectorMatrixStep({
  sectorId,
  sectorCode,
  sectorName,
  sessionId,
  organizationSize,
  serverAnalysis,
  isSectorCompleted,
  isSectorDefault,
  onAnalysisChange,
  onMatrixStateChange,
  onBaseAnalysisChange,
  unlockRequested,
  onUnlockRequestHandled,
  resetAnalysis,
  onResetHandled,
}: SectorMatrixStepProps) {
  const isMobile = useIsMobile();
  const [showUnlockDialog, setShowUnlockDialog] = useState(false);

  const { data: defaultMatrixData, isLoading: isLoadingDefaults } =
    useGetDefaultMatrix(sessionId, sectorId);

  const [defaultAnalysis, setDefaultAnalysis] = useState<SectorAnalysis | null>(
    null
  );

  useEffect(() => {
    if (!defaultMatrixData || !organizationSize) return;

    const attackMatrix = processMatrixData(defaultMatrixData, organizationSize);
    const { actorTotals, totalScore, cyfunLevel } =
      recalculateAnalysisMetrics(attackMatrix);

    setDefaultAnalysis({
      riskAnalysisId: sectorId,
      sectorId,
      sectorCode,
      sectorName,
      attackMatrix,
      actorTotals,
      totalScore,
      cyfunLevel,
      isComplete: false,
    });
  }, [defaultMatrixData, organizationSize, sectorId, sectorCode, sectorName]);

  const effectiveServerAnalysis = serverAnalysis ?? defaultAnalysis;

  const {
    localAnalysis,
    hasLocalChanges,
    isDefault,
    isLocked,
    setIsLocked,
    handleProbabilityChange,
    handleImpactChange,
    resetToDefault,
    applyExternalAnalysis,
  } = useSectorMatrixLocalState({
    sectorId,
    serverAnalysis: effectiveServerAnalysis,
    defaultAnalysis: defaultAnalysis ?? serverAnalysis,
    organizationSize,
    initialLocked: isSectorDefault,
  });

  useEffect(() => {
    onAnalysisChange(localAnalysis);
  }, [localAnalysis, onAnalysisChange]);

  useEffect(() => {
    onMatrixStateChange({ isLocked, isDefault, hasChanges: hasLocalChanges });
  }, [isLocked, isDefault, hasLocalChanges, onMatrixStateChange]);

  const baseAnalysis = defaultAnalysis ?? serverAnalysis;

  useEffect(() => {
    onBaseAnalysisChange(baseAnalysis);
  }, [baseAnalysis, onBaseAnalysisChange]);

  useEffect(() => {
    if (unlockRequested) {
      setShowUnlockDialog(true);
      onUnlockRequestHandled();
    }
  }, [unlockRequested, onUnlockRequestHandled]);

  useEffect(() => {
    if (resetAnalysis) {
      applyExternalAnalysis(resetAnalysis);
      onResetHandled();
    }
  }, [resetAnalysis, applyExternalAnalysis, onResetHandled]);

  const currentDiff = useMemo(() => {
    const baseAnalysis = defaultAnalysis ?? serverAnalysis;
    if (!baseAnalysis || !localAnalysis) return null;
    return compareSectorAnalysis(baseAnalysis, localAnalysis);
  }, [defaultAnalysis, serverAnalysis, localAnalysis]);

  if (!localAnalysis) {
    return <RiskMatrixSkeleton isMobile={isMobile} />;
  }

  const handleUnlockConfirm = () => {
    setIsLocked(false);
    setShowUnlockDialog(false);
  };

  return (
    <div className="space-y-3 md:space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 md:gap-4">
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Scor Total:</span>
            <span className="text-base font-bold md:text-lg">
              {formatScore(localAnalysis.totalScore)}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Nivel CyFun:</span>
            <CyFunLevelBadge level={localAnalysis.cyfunLevel} />
          </div>
        </div>
      </div>

      {isLocked && !(isSectorCompleted && !isSectorDefault) && (
        <Alert className="bg-blue-50 border-blue-200 border-l-4 border-l-blue-500">
          <Lock className="h-5 w-5 text-blue-600" />
          <AlertTitle className="text-blue-900">
            Valori Implicite Recomandate
          </AlertTitle>
          <AlertDescription className="text-blue-800 text-xs md:text-sm">
            {isSectorCompleted
              ? "Aceste valori sunt recomandate de DNSC pentru acest sector. Puteți debloca pentru modificări."
              : "Aceste valori sunt recomandate de DNSC pentru acest sector. Le puteti salva direct sau debloca pentru modificari."}
          </AlertDescription>
        </Alert>
      )}

      {isSectorCompleted && !isSectorDefault && (
        <Alert className="bg-blue-50 border-blue-200 border-l-4 border-l-blue-500">
          <Info className="h-5 w-5 text-blue-600" />
          <AlertTitle className="text-blue-900">Valori Modificate</AlertTitle>
          <AlertDescription className="text-blue-800 text-xs md:text-sm">
            Valorile au fost modificate față de cele recomandate de DNSC.
            Înainte de a genera raportul va trebui să adăugați justificări
            pentru modificările făcute.
          </AlertDescription>
        </Alert>
      )}

      {hasLocalChanges && !isLocked && (
        <Alert className="bg-amber-50 border-amber-200 border-l-4 border-l-amber-500">
          <AlertCircle className="h-5 w-5 text-amber-600" />
          <AlertTitle className="text-amber-900">Salvare necesara</AlertTitle>
          <AlertDescription className="text-amber-800 text-xs md:text-sm">
            Aveti modificari nesalvate. Navigarea la pasul urmator va salva
            automat modificarile.
          </AlertDescription>
        </Alert>
      )}

      <div>
        {isMobile ? (
          <RiskMatrixCarousel
            attackMatrix={localAnalysis.attackMatrix}
            actorTotals={localAnalysis.actorTotals}
            totalScore={localAnalysis.totalScore}
            orgSize={organizationSize}
            onProbabilityChange={handleProbabilityChange}
            onImpactChange={handleImpactChange}
            diff={currentDiff}
            disabled={isLocked}
          />
        ) : (
          <RiskMatrixTable
            sectorName={sectorName}
            attackMatrix={localAnalysis.attackMatrix}
            actorTotals={localAnalysis.actorTotals}
            totalScore={localAnalysis.totalScore}
            orgSize={organizationSize}
            onProbabilityChange={handleProbabilityChange}
            onImpactChange={handleImpactChange}
            cyfunLevel={localAnalysis.cyfunLevel}
            diff={currentDiff}
            disabled={isLocked}
          />
        )}
      </div>

      <AlertDialog open={showUnlockDialog} onOpenChange={setShowUnlockDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deblocare matrice de risc</AlertDialogTitle>
            <AlertDialogDescription>
              Modificarile aduse valorilor implicite trebuie justificate conform
              reglementarilor DNSC. Sunteti sigur ca doriti sa modificati
              valorile?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Anulează</AlertDialogCancel>
            <AlertDialogAction onClick={handleUnlockConfirm}>
              Deblocați
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
